'use client';

import { Trans } from '@lingui/macro';
import { Box, Stack, Typography } from '@mui/material';
import { createApiClient } from '@prismo-io/core';
import { Button, DiscCDPalettes, DiscCPalettes, DiscDIPalettes, DiscDPalettes, DiscIPalettes, DiscISPalettes, DiscSCPalettes, DiscSPalettes, KeyWordChip, MarkdownRenderer, SectionArticle } from '@prismo-io/design-system';
import { AuraProfileCode, type AuraProfileCodeT, LanguageCode, type LanguageCodeT, UserAccessType, type UserAccessTypeT } from '@prismo-io/schemas';
import Image from 'next/image';
import { useEffect, useMemo, useState } from 'react';
import { z } from 'zod';
import AdvisorIllustration from '@/assets/aura/app/advisor/common/profile.svg';
import AnalystIllustration from '@/assets/aura/app/analyst/common/profile.svg';
import AnimatorIllustration from '@/assets/aura/app/animator/common/profile.svg';
import CoachIllustration from '@/assets/aura/app/coach/common/profile.svg';
import PeacemakerIllustration from '@/assets/aura/app/peacemaker/common/profile.svg';
import PilotIllustration from '@/assets/aura/app/pilot/common/profile.svg';
import PlannerIllustration from '@/assets/aura/app/planner/common/profile.svg';
import ProtectorIllustration from '@/assets/aura/app/protector/common/profile.svg';
import { useLingui } from '@lingui/react';
import { ThemeProvider } from '@prismo-io/design-system/theme-provider';
import { TeasingCard } from '@prismo-io/feature-me/components/teasing-card';
type Props = {
  profileType: AuraProfileCodeT;
  access?: UserAccessTypeT;
  local?: LanguageCodeT;
};
type ProfileCommunicationRecord = Record<AuraProfileCodeT, {
  // keyWords: string[];
  // keyWordsTitle: string;
  // desc: string;
  img: any;
  color: any;
}>;
type ProfileCommunication = ProfileCommunicationRecord[AuraProfileCodeT];
const fakedata = {
  keyWords: ['Amour', 'Gloire', 'Beauté'],
  desc: `There's something that doesn't make sense. Let's go and poke it with a stick. Brave heart, Clara. I need...I need...I need... fish fingers and custard! There are fixed points throughout time where things must stay exactly the way they are. This is not one of them. This is an opportunity! Whatever happens here will create its own timeline, its own reality, a temporal tipping point. The future revolves around you, here, now, so do good! I never know why. I only know who. Father Christmas. Santa Claus. Or, as I’ve always known him, Jeff.`
};
const CommunicateWithSchema = z.object({
  id: z.string(),
  title: z.string(),
  content: z.string(),
  keywords: z.string().array()
}).nullable();
type CommunicateWithSchemaT = z.infer<typeof CommunicateWithSchema>;
export const CommunicateWith = (props: Props) => {
  const {
    profileType,
    access = UserAccessType.enum.FREE,
    local = LanguageCode.Enum.fr
  } = props;
  const isBlury = useMemo(() => access === UserAccessType.enum.STARTER || access === UserAccessType.enum.EXPERT_FREE || access === UserAccessType.enum.LEAD, [access]);
  const blurFilter = useMemo(() => isBlury ? 'blur(4px)' : 'blur(0)', [isBlury]);
  const {
    i18n
  } = useLingui();
  const profileCommunications = useMemo((): ProfileCommunicationRecord => ({
    ADVISOR: {
      img: AdvisorIllustration,
      color: DiscSPalettes.light
    },
    ANALYST: {
      img: AnalystIllustration,
      color: DiscCPalettes.light
    },
    ANIMATOR: {
      img: AnimatorIllustration,
      color: DiscIPalettes.light
    },
    COACH: {
      img: CoachIllustration,
      color: DiscDIPalettes.light
    },
    PEACEMAKER: {
      img: PeacemakerIllustration,
      color: DiscISPalettes.light
    },
    PILOT: {
      img: PilotIllustration,
      color: DiscDPalettes.light
    },
    PLANNER: {
      img: PlannerIllustration,
      color: DiscCDPalettes.light
    },
    PROTECTOR: {
      img: ProtectorIllustration,
      color: DiscSCPalettes.light
    }
  }), [i18n.locale]);
  const [choosenProfile, setChoosenProfile] = useState<AuraProfileCodeT>(profileType);
  const profileCom = useMemo((): ProfileCommunication => {
    return profileCommunications[choosenProfile];
  }, [profileCommunications, choosenProfile]);
  const [communicateWithType, setCommunicateWithType] = useState<string>(`communicate-with-${profileType.toLowerCase()}`);
  const onChoosenProfile = (profile: AuraProfileCodeT) => {
    return () => {
      setCommunicateWithType(`communicate-with-${profile.toLowerCase()}`);
      setChoosenProfile(profile);
    };
  };
  const client = createApiClient();
  const [communicateWithData, setCommunicateWithData] = useState<CommunicateWithSchemaT>(null);
  const fetchCommunicateWithData = async (type: string) => {
    try {
      const {
        data,
        error
      } = await client.from('reading_keys').select(`
      id,
      title,
      content,
      keywords
    `).eq('language_id', local).eq('key', type).limit(1).maybeSingle();
      if (error) throw error;
      setCommunicateWithData(CommunicateWithSchema.parse(data));
    } catch (error) {
      console.error('Erreur de récupération', error);
    }
  };
  useEffect(() => {
    fetchCommunicateWithData(communicateWithType);
  }, [communicateWithType]);
  return <Stack direction="column" spacing={3} position="relative" data-sentry-element="Stack" data-sentry-component="CommunicateWith" data-sentry-source-file="index.tsx">
      {isBlury && <TeasingCard access={access} />}
      <Box component="div" position="absolute" right={0} top={0} zIndex={1} width={{
      none: '250px',
      lg: '250px'
    }} height={{
      none: '250px',
      lg: '100%'
    }} sx={{
      display: {
        none: 'none',
        md: 'block'
      }
    }} data-sentry-element="Box" data-sentry-source-file="index.tsx">
        <Image src={profileCom.img} alt="" style={{
        objectFit: 'fill',
        objectPosition: 'center'
      }} fill data-sentry-element="Image" data-sentry-source-file="index.tsx" />
      </Box>
      <Box component="header" p={{
      none: '0',
      md: '0 1.5rem',
      lg: '0 4rem'
    }} data-sentry-element="Box" data-sentry-source-file="index.tsx">
        <Typography variant="h3" data-sentry-element="Typography" data-sentry-source-file="index.tsx">
          <Trans data-sentry-element="Trans" data-sentry-source-file="index.tsx">Comment communiquer avec les autres profils</Trans>
        </Typography>
      </Box>
      {/* EXEMPLE DE BREAKPOINTS */}
      <Stack direction="row" flexWrap="wrap"
    // spacing={2}
    gap={2} maxWidth={{
      none: '100%',
      md: '75%',
      xl: '60%'
    }} p={{
      none: '0',
      md: '0 1.5rem',
      lg: '0 4rem'
    }} data-sentry-element="Stack" data-sentry-source-file="index.tsx">
        <ThemeProvider theme={DiscDPalettes.light} options={{
        key: 'communicate-with-pilot-btn'
      }} data-sentry-element="ThemeProvider" data-sentry-source-file="index.tsx">
          <Button variant="contained" color="gradient" sx={{
          '& .MuiButton-startIcon': {
            display: 'none'
          }
        }} onClick={onChoosenProfile(AuraProfileCode.Enum.PILOT)} data-sentry-element="Button" data-sentry-source-file="index.tsx">
            <Trans data-sentry-element="Trans" data-sentry-source-file="index.tsx">Pilote</Trans>
          </Button>
        </ThemeProvider>
        <ThemeProvider theme={DiscDIPalettes.light} options={{
        key: 'communicate-with-coach-btn'
      }} data-sentry-element="ThemeProvider" data-sentry-source-file="index.tsx">
          <Button variant="contained" color="gradient" sx={{
          '& .MuiButton-startIcon': {
            display: 'none'
          }
        }} onClick={onChoosenProfile(AuraProfileCode.Enum.COACH)} data-sentry-element="Button" data-sentry-source-file="index.tsx">
            <Trans data-sentry-element="Trans" data-sentry-source-file="index.tsx">Entraîneur</Trans>
          </Button>
        </ThemeProvider>
        <ThemeProvider theme={DiscIPalettes.light} options={{
        key: 'communicate-with-animator-btn'
      }} data-sentry-element="ThemeProvider" data-sentry-source-file="index.tsx">
          <Button variant="contained" color="gradient" sx={{
          '& .MuiButton-startIcon': {
            display: 'none'
          }
        }} onClick={onChoosenProfile(AuraProfileCode.Enum.ANIMATOR)} data-sentry-element="Button" data-sentry-source-file="index.tsx">
            <Trans data-sentry-element="Trans" data-sentry-source-file="index.tsx">Animateur</Trans>
          </Button>
        </ThemeProvider>
        <ThemeProvider theme={DiscISPalettes.light} options={{
        key: 'communicate-with-peacemaker-btn'
      }} data-sentry-element="ThemeProvider" data-sentry-source-file="index.tsx">
          <Button variant="contained" color="gradient" sx={{
          '& .MuiButton-startIcon': {
            display: 'none'
          }
        }} onClick={onChoosenProfile(AuraProfileCode.Enum.PEACEMAKER)} data-sentry-element="Button" data-sentry-source-file="index.tsx">
            <Trans data-sentry-element="Trans" data-sentry-source-file="index.tsx">Pacificateur</Trans>
          </Button>
        </ThemeProvider>
        <ThemeProvider theme={DiscSPalettes.light} options={{
        key: 'communicate-with-advisor-btn'
      }} data-sentry-element="ThemeProvider" data-sentry-source-file="index.tsx">
          <Button variant="contained" color="gradient" sx={{
          '& .MuiButton-startIcon': {
            display: 'none'
          }
        }} onClick={onChoosenProfile(AuraProfileCode.Enum.ADVISOR)} data-sentry-element="Button" data-sentry-source-file="index.tsx">
            <Trans data-sentry-element="Trans" data-sentry-source-file="index.tsx">Conseiller</Trans>
          </Button>
        </ThemeProvider>
        <ThemeProvider theme={DiscSCPalettes.light} options={{
        key: 'communicate-with-protector-btn'
      }} data-sentry-element="ThemeProvider" data-sentry-source-file="index.tsx">
          <Button variant="contained" color="gradient" sx={{
          '& .MuiButton-startIcon': {
            display: 'none'
          }
        }} onClick={onChoosenProfile(AuraProfileCode.Enum.PROTECTOR)} data-sentry-element="Button" data-sentry-source-file="index.tsx">
            <Trans data-sentry-element="Trans" data-sentry-source-file="index.tsx">Protecteur</Trans>
          </Button>
        </ThemeProvider>
        <ThemeProvider theme={DiscCPalettes.light} options={{
        key: 'communicate-with-analyst-btn'
      }} data-sentry-element="ThemeProvider" data-sentry-source-file="index.tsx">
          <Button variant="contained" color="gradient" sx={{
          '& .MuiButton-startIcon': {
            display: 'none'
          }
        }} onClick={onChoosenProfile(AuraProfileCode.Enum.ANALYST)} data-sentry-element="Button" data-sentry-source-file="index.tsx">
            <Trans data-sentry-element="Trans" data-sentry-source-file="index.tsx">Analyste</Trans>
          </Button>
        </ThemeProvider>
        <ThemeProvider theme={DiscCDPalettes.light} options={{
        key: 'communicate-with-planner-btn'
      }} data-sentry-element="ThemeProvider" data-sentry-source-file="index.tsx">
          <Button variant="contained" color="gradient" sx={{
          '& .MuiButton-startIcon': {
            display: 'none'
          }
        }} onClick={onChoosenProfile(AuraProfileCode.Enum.PLANNER)} data-sentry-element="Button" data-sentry-source-file="index.tsx">
            <Trans data-sentry-element="Trans" data-sentry-source-file="index.tsx">Planificateur</Trans>
          </Button>
        </ThemeProvider>
      </Stack>
      <ThemeProvider theme={profileCom.color} options={{
      key: 'communicate-with-description'
    }} data-sentry-element="ThemeProvider" data-sentry-source-file="index.tsx">
        <SectionArticle minHeight="224px" backgroundColor="background.primary" justifyContent="space-between" data-sentry-element="SectionArticle" data-sentry-source-file="index.tsx">
          <MarkdownRenderer wrapper={{
          component: 'div',
          maxWidth: {
            none: '100%',
            md: '80%'
          },
          sx: {
            filter: blurFilter
          }
        }} data-sentry-element="MarkdownRenderer" data-sentry-source-file="index.tsx">
            {isBlury ? fakedata.desc : communicateWithData?.content}
          </MarkdownRenderer>
          <Stack direction="row" justifyContent="center" data-sentry-element="Stack" data-sentry-source-file="index.tsx">
            <Box component="div" width="50%" minWidth="100px" height="1px" borderTop={'1px solid'} sx={{
            borderColor: 'primary.main'
          }} data-sentry-element="Box" data-sentry-source-file="index.tsx" />
          </Stack>
          <Stack direction="column" spacing={2} alignItems="center" data-sentry-element="Stack" data-sentry-source-file="index.tsx">
            <Typography variant="h4" data-sentry-element="Typography" data-sentry-source-file="index.tsx">{communicateWithData?.title}</Typography>
            <Stack direction="row" justifyContent="center" flexWrap="wrap" gap={2} sx={{
            filter: blurFilter
          }} data-sentry-element="Stack" data-sentry-source-file="index.tsx">
              {isBlury ? fakedata.keyWords.map((word, index) => {
              return <KeyWordChip key={`${word}-${index}`} keyWord={word} />;
            }) : communicateWithData?.keywords.map((word, index) => {
              return <KeyWordChip key={`${word}-${index}`} keyWord={word} />;
            })}
            </Stack>
            {/* <Stack
              direction="row"
              justifyContent="center"
              flexWrap="wrap"
              gap={2}
              sx={{ filter: blurFilter }}
             >
              {isBlury
                ? fakedata.keyWords.map((word, index) => {
                    return <KeyWordChip key={`${word}-${index}`} keyWord={word} />;
                  })
                : profileCom.keyWords.map((word, index) => {
                    return <KeyWordChip key={`${word}-${index}`} keyWord={word} />;
                  })}
             </Stack> */}
          </Stack>
        </SectionArticle>
      </ThemeProvider>
    </Stack>;
};